/**
 * SegmentedToggle — the bordered pill of stencil-labelled segments used by the
 * map bar's GAME | REGIONS view switch and the chat bar's ALL | CHAT log
 * filter.
 *
 * `badgeCount` puts a count bubble on the pill's top-right corner (the chat
 * filter's unread count sits over its CHAT segment, the last one).
 */
import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { CountBubble } from './CountBubble'

export interface SegmentOption<T extends string> {
  value: T
  label: string
  /** Defaults to "Show <label>". */
  accessibilityLabel?: string
  testID?: string
}

export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  size,
  minWidth,
  compact = false,
  badgeCount = 0,
  badgeAccessibilityLabel,
  testID,
}: {
  options: readonly SegmentOption<T>[]
  /** Unset: no segment selected. */
  value: T | undefined
  onChange: (value: T) => void
  /** Segment height (the map bar's control size). */
  size: number
  minWidth: number
  compact?: boolean
  badgeCount?: number
  badgeAccessibilityLabel?: string
  testID?: string
}) {
  return (
    <View testID={testID}>
      <View className="flex-row border border-olive rounded-md" style={{ overflow: 'hidden' }}>
        {options.map((option, index) => {
          const active = option.value === value
          return (
            <React.Fragment key={option.value}>
              {index > 0 ? <View style={{ width: 1 }} className="bg-olive" /> : null}
              <TouchableOpacity
                onPress={() => onChange(option.value)}
                accessibilityRole="button"
                accessibilityLabel={option.accessibilityLabel ?? `Show ${option.label.toLowerCase()}`}
                accessibilityState={{ selected: active }}
                testID={option.testID}
                className={`${compact ? 'px-1.5' : 'px-2.5'} items-center justify-center ${active ? 'bg-blood' : 'bg-paper'}`}
                style={{ height: size, minWidth, justifyContent: 'center' }}
              >
                <Text
                  className={`font-stencil ${compact ? 'text-[9px]' : 'text-[10px]'} uppercase ${active ? 'text-white' : 'text-ink'}`}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            </React.Fragment>
          )
        })}
      </View>
      {badgeCount > 0 ? (
        <View pointerEvents="none" style={{ position: 'absolute', top: -6, right: -6, zIndex: 1 }}>
          <CountBubble count={badgeCount} testID={testID ? `${testID}-unread` : undefined} accessibilityLabel={badgeAccessibilityLabel ?? `${badgeCount} unread`} />
        </View>
      ) : null}
    </View>
  )
}
